// Store layout: sidebar (Padmora brand, Store home, search, categories, separator, orden) + main content
// On mobile: sidebar is a drawer; header shows menu trigger + search so both are always accessible.

import { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCategories, useProductFamilyCategories, useClientLead } from '@/hooks/use-store';
import { supabase } from '@/lib/supabase';
import { postWorkerApi } from '@/lib/worker-api';
import { safeTrim } from '@/lib/utils';
import { useQuotationCart } from '@/hooks/use-quotation-cart';
import { useIsMobile } from '@/hooks/use-mobile';
import { StoreSearchResults } from '@/components/store/StoreSearchResults';
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupLabel, SidebarGroupContent, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarSeparator, SidebarTrigger } from '@/components/ui/sidebar';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { ShoppingCart, Search, Layers, Home, User, History, LogOut } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const StoreLayout = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { data: categories, isLoading } = useCategories();
  const { data: familyCategories } = useProductFamilyCategories();
  const { data: clientLead, isLoading: leadLoading } = useClientLead();
  // En vista cliente: no mostrar categorías sin familias en el sidebar (modo oculto)
  const categoryIdsWithFamilies = familyCategories ? new Set(familyCategories.map((r) => r.category_id)) : null;
  const sidebarCategories =
    categoryIdsWithFamilies == null ? (categories ?? []) : (categories ?? []).filter((cat) => categoryIdsWithFamilies.has(cat.id));
  const { items: cartItems } = useQuotationCart();
  const isLoggedIn = !!clientLead;
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOverlayOpen, setSearchOverlayOpen] = useState(false);
  const cartCount = cartItems.reduce((acc, i) => acc + i.quantity, 0);
  const linkedLeadRef = useRef<string | null>(null);

  const hasSearchQuery = safeTrim(searchQuery).length >= 2;

  // If user is logged in (Supabase session) but has no lead linked (e.g. admin), try to link a lead by email once.
  useEffect(() => {
    if (clientLead != null || leadLoading) return;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id || !session.access_token) return;
      if (linkedLeadRef.current === session.user.id) return;
      linkedLeadRef.current = session.user.id;
      const res = await postWorkerApi<{ linked?: boolean }>('/api/link-lead-by-auth', {
        access_token: session.access_token,
      });
      if (res.ok && res.data?.linked) {
        queryClient.invalidateQueries({ queryKey: ['store', 'client'] });
      }
    })();
  }, [clientLead, leadLoading, queryClient]);

  useEffect(() => {
    if (!clientLead) linkedLeadRef.current = null;
  }, [clientLead]);

  // When you navigate to a product/cart page, close the search overlay so you see the content.
  useEffect(() => {
    if (
      pathname.startsWith('/store/product') ||
      pathname === '/store/cart' ||
      pathname === '/store/cart/request' ||
      pathname === '/store/cart/success' ||
      pathname === '/store/profile' ||
      pathname === '/store/quotations'
    ) {
      setSearchOverlayOpen(false);
    }
  }, [pathname]);

  const isOnStoreHome = pathname === '/store';
  // Search results only when user explicitly runs search with Enter (searchOverlayOpen).
  // Clicking the search input lets you type from any page; Enter triggers the search.
  const showSearchResults = hasSearchQuery && searchOverlayOpen;

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && hasSearchQuery) {
      setSearchOverlayOpen(true);
    }
  };

  // Verification page: no sidebar, full-screen focused flow
  if (pathname === '/store/newclientidentification') {
    return <Outlet />;
  }

  return (
    <SidebarProvider>
      <Sidebar className="border-r border-sidebar-border bg-sidebar">
        <SidebarContent className="flex flex-col gap-0">
          {/* Brand: compact header, logo + name */}
          <div className="px-3 py-4">
            <Link
              to="/"
              className="flex items-center gap-3 rounded-lg px-2 py-1.5 -mx-1 transition-colors hover:bg-sidebar-accent"
            >
              <div className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden shrink-0 ring-1 ring-[#1451af]/30">
                <img src="/logo.png" alt="Padmora" className="w-full h-full object-cover" />
              </div>
              <span className="text-lg font-semibold text-[#1451af] tracking-tight">Padmora</span>
            </Link>
          </div>

          <SidebarSeparator className="mx-2" />

          {/* Navegación: home + búsqueda */}
          <SidebarGroup className="py-2">
            <SidebarGroupLabel className="px-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Navegación
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={isOnStoreHome}>
                    <Link to="/store" className="flex items-center gap-2.5 w-full">
                      <Home className="h-4 w-4 shrink-0" />
                      <span>Store home</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem className="px-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="search"
                      placeholder="Buscar productos…"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      className="pl-8 h-9 bg-background/80 border-sidebar-border text-sm"
                    />
                  </div>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarSeparator className="mx-2" />

          {/* Categorías */}
          <SidebarGroup className="flex-1 overflow-auto py-2">
            <SidebarGroupLabel className="px-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Categorías
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {isLoading ? (
                  <SidebarMenuItem>
                    <Skeleton className="h-8 w-full rounded-md" />
                  </SidebarMenuItem>
                ) : sidebarCategories.length === 0 ? (
                  <SidebarMenuItem>
                    <p className="px-3 py-2 text-xs text-muted-foreground">
                      Estamos cargando productos. Pronto verá productos aquí.
                    </p>
                  </SidebarMenuItem>
                ) : (
                  sidebarCategories.map((cat) => (
                    <SidebarMenuItem key={cat.id}>
                      <SidebarMenuButton
                        asChild
                        isActive={pathname.includes(`/store/category/${cat.slug}`)}
                      >
                        <Link to={`/store/category/${cat.slug}`} className="flex items-center gap-2.5 w-full">
                          <Layers className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="truncate text-sm">{cat.name}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarSeparator className="mx-2" />

          {/* Cotización: un solo ítem destacado */}
          <SidebarGroup className="py-2">
            <SidebarGroupLabel className="px-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Cotización
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={pathname === '/store/cart'}>
                    <Link to="/store/cart" className="flex items-center gap-2.5 w-full">
                      <ShoppingCart className="h-4 w-4 shrink-0" />
                      <span className="flex-1 truncate">Orden de cotización</span>
                      {cartCount > 0 && (
                        <Badge variant="secondary" className="shrink-0 min-w-5 h-5 px-1.5 text-xs justify-center">
                          {cartCount}
                        </Badge>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Cuenta: perfil, historial, cerrar sesión */}
          <SidebarGroup className="py-2">
            <SidebarGroupLabel className="px-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Cuenta
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {isLoggedIn ? (
                  <>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild isActive={pathname === '/store/profile'}>
                        <Link to="/store/profile" className="flex items-center gap-2.5 w-full">
                          <User className="h-4 w-4 shrink-0" />
                          <span>Mi perfil</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild isActive={pathname.startsWith('/store/quotations')}>
                        <Link to="/store/quotations" className="flex items-center gap-2.5 w-full">
                          <History className="h-4 w-4 shrink-0" />
                          <span>Historial cotizaciones</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        className="flex items-center gap-2.5 w-full text-muted-foreground hover:text-sidebar-foreground"
                        onClick={async () => {
                          await supabase.auth.signOut();
                          window.dispatchEvent(new Event('quotation-cart-cleared'));
                          navigate('/store', { replace: true });
                        }}
                      >
                        <LogOut className="h-4 w-4 shrink-0" />
                        <span>Cerrar sesión</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </>
                ) : (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={pathname === '/store/login'}>
                      <Link to="/store/login" className="flex items-center gap-2.5 w-full">
                        <User className="h-4 w-4 shrink-0" />
                        <span>Iniciar sesión</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
      <SidebarInset>
        {/* Mobile: header with menu (opens sidebar) + search so both are visible without opening drawer */}
        {isMobile && (
          <header className="flex shrink-0 items-center gap-2 border-b border-zinc-200 bg-background px-4 py-3">
            <SidebarTrigger aria-label="Abrir menú" className="shrink-0" />
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400 shrink-0" />
              <Input
                type="search"
                placeholder="Buscar... (Enter)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="h-9 pl-8 bg-zinc-50 border-zinc-200"
              />
            </div>
          </header>
        )}
        <main className="min-h-0 flex-1 overflow-auto p-6">
          {/* Wrapper with min height so content area is visible in Chrome/mobile (flex min-height can miscompute) */}
          <div className="min-h-[50vh]">
            {showSearchResults ? (
              <StoreSearchResults query={searchQuery} />
            ) : (
              <Outlet />
            )}
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default StoreLayout;
